import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { ACT_AS_HEADER } from "../../utils/actAs";
import type { MeBalanceSource } from "../../utils/balance";
import type { FollowUser, PaginatedUsersData } from "./follows";
import type {
  TransactionsResponse,
  WithdrawableTransactionsResponse,
} from "./transactions";
import type {
  CreatePaymentRequestResponse,
} from "./paymentRequests";

/**
 * アカウント管理ページ（/account）用 API スライス。
 *
 * - 一覧: `GET /api/users/?role=`（writer 権限 = ADMIN / MANAGER）
 * - 行単位の詳細（振込申請・進行中モーダル）: 既存の `/api/me` 系 API を
 *   `X-Act-As-User` ヘッダー付き（per-request）で呼び、対象ユーザー本人として
 *   データを取得する。キャッシュは userId 引数でキー分離される。
 */

/** アカウント管理ページで扱うグローバルロール（API 値） */
export type AccountRole = "CREATOR" | "CLIENT" | "ADMIN" | "MANAGER";

/** アカウント状態（`GET /api/users/?status=` と一覧行の `account_status`） */
export type AccountStatus = "active" | "invited";

/** `GET /api/users/?role=` の 1 行（共通ユーザー要約 + 管理用フィールド） */
export interface AccountUser extends FollowUser {
  /** AuthUser.modified（最終更新日時、ISO 文字列） */
  updated_at: string | null;
  /** メールアドレス（role 指定の管理一覧のみ返る） */
  email?: string | null;
  /** 振込申請できる settled 残高（>= ¥100）があるか */
  has_withdrawable?: boolean | null;
  /** 進行中取引（ドラフト除く未完了オファー）があるか。CREATOR 一覧のみ判定 */
  has_in_progress?: boolean | null;
  /** アカウント状態（active = 有効 / invited = 招待中の未登録） */
  account_status?: AccountStatus | "deleted";
}

/** 一覧からインライン編集できるフィールド（act-as PATCH /api/me） */
export interface AccountUserPatch {
  display_name_jp?: string;
  display_name_en?: string;
  title_jp?: string;
  title_en?: string;
  affiliation_jp?: string;
  affiliation_en?: string;
}

/** 一覧の列ヘッダーソートで指定できるキー（BE `ACCOUNT_LIST_SORT_FIELDS` と対応） */
export type AccountSortField =
  | "display_name_jp"
  | "display_name_en"
  | "title_jp"
  | "title_en"
  | "affiliation_jp"
  | "affiliation_en"
  | "email"
  | "updated_at";

export interface AccountsData extends Omit<PaginatedUsersData, "users"> {
  users: AccountUser[];
}

export interface AccountsResponse {
  status: string;
  data: AccountsData;
}

export interface GetAccountsParams {
  role: AccountRole;
  q?: string;
  limit?: number;
  offset?: number;
  /** 列ヘッダーソート（省略時はサーバー既定 = 最終更新日降順） */
  sort?: AccountSortField;
  order?: "asc" | "desc";
  /** アカウント状態（省略時はサーバー既定 = active） */
  status?: AccountStatus;
}

/**
 * 対象ユーザーとして取得した `GET /api/me` のデータ。
 * 残高（振込申請モーダル）と銀行口座の登録チェックに使う最小型。
 */
export interface AccountMeData extends MeBalanceSource {
  id: number;
  role?: string;
  display_name_jp?: string | null;
  display_name_en?: string | null;
  bank?: string | null;
  bank_branch?: string | null;
  bank_branch_number?: string | null;
  account_type?: string | null;
  account_number?: string | null;
  account_holder?: string | null;
  [key: string]: unknown;
}

export interface AccountMeResponse {
  status: string;
  data: AccountMeData;
}

const buildAccountsQueryString = ({
  role,
  q,
  limit,
  offset,
  sort,
  order,
  status,
}: GetAccountsParams): string => {
  const params: Record<string, string> = { role };
  if (q !== undefined && q !== "") params.q = q;
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  if (sort !== undefined) {
    params.sort = sort;
    if (order !== undefined) params.order = order;
  }
  if (status !== undefined) params.status = status;
  return `?${new URLSearchParams(params).toString()}`;
};

/** 対象ユーザーとして API を呼ぶための per-request ヘッダー */
const actAsHeaders = (userId: number): Record<string, string> => ({
  [ACT_AS_HEADER]: String(userId),
});

export const accountsApi = createApi({
  reducerPath: "accountsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Accounts", "AccountMe", "AccountTransactions"],
  endpoints: (builder) => ({
    /** ロール別アカウント一覧（検索・ページング付き） */
    getAccounts: builder.query<AccountsResponse, GetAccountsParams>({
      query: (params) => `/api/users/${buildAccountsQueryString(params)}`,
      providesTags: (_result, _error, { role, q, limit, offset, status }) => [
        {
          type: "Accounts",
          id: `${role}:${status ?? "active"}:${q ?? ""}:${limit ?? ""}:${offset ?? ""}`,
        },
        "Accounts",
      ],
    }),

    /** 対象ユーザーの `GET /api/me`（残高・銀行口座） */
    getAccountMe: builder.query<AccountMeResponse, number>({
      query: (userId) => ({
        url: "/api/me",
        headers: actAsHeaders(userId),
      }),
      providesTags: (_result, _error, userId) => [
        { type: "AccountMe", id: userId },
      ],
    }),

    /** 対象ユーザーの進行中取引（全プロジェクト横断） */
    getAccountTransactions: builder.query<TransactionsResponse, number>({
      query: (userId) => ({
        url: "/api/me/transactions",
        headers: actAsHeaders(userId),
      }),
      providesTags: (_result, _error, userId) => [
        { type: "AccountTransactions", id: userId },
      ],
      keepUnusedDataFor: 30,
    }),

    /** 対象ユーザーの振込申請対象（検収完了・未申請）一覧 */
    getAccountWithdrawableTransactions: builder.query<
      WithdrawableTransactionsResponse,
      number
    >({
      query: (userId) => ({
        url: "/api/me/withdrawable-transactions",
        headers: actAsHeaders(userId),
      }),
      providesTags: (_result, _error, userId) => [
        { type: "AccountTransactions", id: userId },
      ],
      keepUnusedDataFor: 30,
    }),

    /**
     * 一覧セルのインライン編集（表示名・タイトル・所属）。
     * 対象ユーザー本人としての `PATCH /api/me`（act-as）なので、
     * 本人がマイページで編集したのと同じ挙動になる。
     * 一覧の行はページ側でローカル更新するため Accounts タグは触らない
     * （無限スクロール累積のリセットを避ける）。
     */
    updateAccountUser: builder.mutation<
      AccountMeResponse,
      { userId: number; patch: AccountUserPatch }
    >({
      query: ({ userId, patch }) => ({
        url: "/api/me",
        method: "PATCH",
        headers: actAsHeaders(userId),
        body: patch,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AccountMe", id: userId },
      ],
    }),

    /**
     * ユーザー削除（soft delete）。操作者自身の権限で `DELETE /api/users/<id>`
     * を呼ぶ（act-as ではない）。一覧の行はページ側でローカル除去するため
     * Accounts タグは触らない（無限スクロール累積のリセットを避ける）。
     */
    deleteAccountUser: builder.mutation<
      { status: string; data: { user_id: number } },
      number
    >({
      query: (userId) => ({
        url: `/api/users/${userId}`,
        method: "DELETE",
      }),
    }),

    /** 対象ユーザー本人としての振込申請（本人と同様の操作） */
    createAccountPaymentRequest: builder.mutation<
      CreatePaymentRequestResponse,
      { userId: number; amount?: number }
    >({
      query: ({ userId, amount }) => ({
        url: "/api/me/payment-requests",
        method: "POST",
        headers: actAsHeaders(userId),
        body: amount !== undefined ? { amount } : {},
      }),
      // 申請成功で対象ユーザーの残高・振込対象リストが変わるため、
      // 該当ユーザーのキャッシュを無効化して再取得させる。
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AccountMe", id: userId },
        { type: "AccountTransactions", id: userId },
      ],
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useLazyGetAccountsQuery,
  useGetAccountMeQuery,
  useGetAccountTransactionsQuery,
  useGetAccountWithdrawableTransactionsQuery,
  useUpdateAccountUserMutation,
  useDeleteAccountUserMutation,
  useCreateAccountPaymentRequestMutation,
} = accountsApi;
